/**
 * Capacitor Native Services
 * Platform-specific implementations for iOS and Android
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Detect platform
export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();
export const isIOS = () => getPlatform() === 'ios';
export const isAndroid = () => getPlatform() === 'android';
export const isWeb = () => getPlatform() === 'web';

/**
 * Push Notifications Service
 */
export const PushNotificationService = {
  // Check if push notifications are available
  isAvailable: async () => {
    if (!isNativePlatform()) return false;
    return true;
  },

  // Request permission and register for push notifications
  register: async () => {
    if (!isNativePlatform()) {
      return { success: false, message: 'Push notifications not available on web' };
    }
    
    try {
      let permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }
      
      if (permStatus.receive !== 'granted') {
        return { success: false, message: 'Permission denied' };
      }
      
      await PushNotifications.register();
      return { success: true };
    } catch (error) {
      console.error('Push registration error:', error);
      return { success: false, message: error.message };
    }
  },

  // Add listeners for push notifications
  addListeners: (onRegistration, onNotification, onError) => {
    PushNotifications.addListener('registration', onRegistration);
    PushNotifications.addListener('pushNotificationReceived', onNotification);
    PushNotifications.addListener('pushNotificationActionPerformed', onNotification);
    PushNotifications.addListener('registrationError', onError);
  },

  // Remove all listeners
  removeAllListeners: () => {
    PushNotifications.removeAllListeners();
  }
};

/**
 * Camera Service
 */
export const CameraService = {
  // Take a photo using the camera
  takePhoto: async (options = {}) => {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: options.allowEditing || false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera
      });
      return { success: true, data: image.dataUrl, format: image.format };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Pick image from gallery
  pickFromGallery: async (options = {}) => {
    try {
      const image = await Camera.getPhoto({
        quality: options.quality || 90,
        allowEditing: options.allowEditing || false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos
      });
      return { success: true, data: image.dataUrl, format: image.format };
    } catch (error) {
      return { success: false, message: error.message };
    }
  }
};

/**
 * File Service
 */
export const FileService = {
  // Write file to device storage
  writeFile: async (path, data, directory = Directory.Documents) => {
    try {
      await Filesystem.writeFile({
        path,
        data,
        directory
      });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Read file from device storage
  readFile: async (path, directory = Directory.Documents) => {
    try {
      const result = await Filesystem.readFile({
        path,
        directory
      });
      return { success: true, data: result.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete file from device storage
  deleteFile: async (path, directory = Directory.Documents) => {
    try {
      await Filesystem.deleteFile({
        path,
        directory
      });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Check if file exists
  fileExists: async (path, directory = Directory.Documents) => {
    try {
      await Filesystem.stat({ path, directory });
      return true;
    } catch {
      return false;
    }
  }
};

/**
 * Share Service
 */
export const ShareService = {
  // Share content using native share dialog
  share: async (options) => {
    try {
      await Share.share({
        title: options.title,
        text: options.text,
        url: options.url,
        dialogTitle: options.dialogTitle || 'Share'
      });
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Check if sharing is available
  canShare: async () => {
    try {
      const result = await Share.canShare();
      return result.value;
    } catch {
      return false;
    }
  }
};

/**
 * Device Info
 */
export const DeviceInfo = {
  getPlatformInfo: () => ({
    platform: getPlatform(),
    isNative: isNativePlatform(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isWeb: isWeb()
  })
};

/**
 * Timezone Service - Works on Web, iOS, and Android
 * Handles all US timezones and international timezones
 */
export const TimezoneService = {
  /**
   * Get current device timezone
   * Works on: Web browsers, iOS (Capacitor), Android (Capacitor)
   * Returns IANA timezone like "America/New_York", "America/Los_Angeles", etc.
   */
  getDeviceTimezone: () => {
    try {
      // This works on ALL platforms (Web, iOS WebView, Android WebView)
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('📍 Device timezone detected:', timezone);
      return timezone || 'UTC';
    } catch (e) {
      console.warn('Could not detect timezone:', e);
      return 'UTC';
    }
  },

  /**
   * Get user-friendly timezone display name
   */
  getTimezoneDisplayName: (timezone) => {
    const tzNames = {
      // US Timezones
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HT)',
      'America/Phoenix': 'Arizona Time (MST)',
      // International
      'UTC': 'UTC',
      'Europe/London': 'London (GMT/BST)',
      'Europe/Paris': 'Paris (CET)',
      'Asia/Tokyo': 'Tokyo (JST)',
      'Asia/Kolkata': 'India (IST)',
      'Australia/Sydney': 'Sydney (AEST)',
    };
    return tzNames[timezone] || timezone.replace(/_/g, ' ').split('/').pop();
  },

  /**
   * Get current local time in user's timezone
   */
  getCurrentLocalTime: (timezone) => {
    try {
      return new Date().toLocaleString('en-US', { timeZone: timezone });
    } catch (e) {
      return new Date().toLocaleString();
    }
  },

  /**
   * Check if timezone has changed (travel detection)
   */
  hasTimezoneChanged: (storedTimezone) => {
    const currentTimezone = TimezoneService.getDeviceTimezone();
    return currentTimezone !== storedTimezone;
  }
};

export default {
  PushNotificationService,
  CameraService,
  FileService,
  ShareService,
  DeviceInfo,
  TimezoneService,
  isNativePlatform,
  getPlatform,
  isIOS,
  isAndroid,
  isWeb
};
